import {
  createArtworkController,
  type ArtworkEnvironment,
} from "../../../packages/pvc-product/artwork-controller";
import {
  createConfiguratorController,
  validateDimension,
  validateQuantity,
  type ConfiguratorController,
  type ConfiguratorSchema,
} from "../../../packages/pvc-product/configurator-controller";
import {
  createDraftController,
  type DraftStorage,
  type PvcDraftSourceSnapshot,
} from "../../../packages/pvc-product/draft-controller";
import { createOrderLineController } from "../../../packages/pvc-product/order-line-controller";
import { createPricingController } from "../../../packages/pvc-product/pricing-controller";
import {
  createProductPageController,
  type ProductPageController,
} from "../../../packages/pvc-product/product-page-controller";
import type { PvcProductPresentation } from "../../../packages/pvc-product/pvc-product-page-view-model";
import type { PvcSignRuntime } from "../../../packages/pvc-product/pvc-sign-route";
import {
  createPvcOrderLineGateway,
  createPvcQuoteGateway,
} from "../../../packages/pvc-product/pvc-shop-api-adapters";
import {
  pvcOrderBindings,
  pvcQuoteBinding,
} from "../../../packages/pvc-product/pvc-shop-api-bindings";
import { createShopApiGraphqlClient } from "../../../packages/pvc-product/shop-api-transport";
import type { PvcStorefrontCartLine } from "./cart-api";
import { createPreorderArtworkGateway } from "./preorder-artwork-api";

export const PVC_STOREFRONT_SCHEMA: ConfiguratorSchema = {
  schemaVersion: 1,
  constraints: {
    minWidthCm: 10,
    maxWidthCm: 300,
    minHeightCm: 10,
    maxHeightCm: 300,
    stepCm: 1,
    minQuantity: 1,
    maxSelfServeQuantity: 100,
  },
  materials: [
    { id: "pvc-3mm", available: true },
    { id: "pvc-5mm", available: true },
  ],
  addons: [
    { id: "double-sided", available: true },
    { id: "holes", available: true },
    { id: "eyelets", available: true },
    { id: "rounded-corners", available: true },
    { id: "file-check", available: true },
    { id: "template-adjust", available: true },
    { id: "full-design", available: true },
    { id: "rush", available: true },
  ],
};

export const PVC_STOREFRONT_PRODUCT: PvcProductPresentation = {
  title: "שלט PVC לפי מידה",
  description: "שלט PVC מודפס בהתאמה אישית. בחרו מידה, חומר, תוספות ותאריך וקבלו מחיר מיידי.",
  proofRequired: false,
  materials: [
    { id: "pvc-3mm", label: "PVC בעובי 3 מ״מ", description: "פתרון קל ונפוץ לשילוט פנים וחוץ." },
    { id: "pvc-5mm", label: "PVC בעובי 5 מ״מ", description: "קשיח ועבה יותר לשלטים שדורשים גוף משמעותי." },
  ],
  addons: [
    { id: "double-sided", label: "הדפסה דו־צדדית" },
    { id: "holes", label: "חורים לתלייה", description: "אפשר לבחור גימור אחד: חורים, עיניות או פינות מעוגלות." },
    { id: "eyelets", label: "עיניות", description: "בחירה זו מחליפה גימור אחר שנבחר." },
    { id: "rounded-corners", label: "פינות מעוגלות", description: "בחירה זו מחליפה גימור אחר שנבחר." },
    { id: "file-check", label: "בדיקת קובץ מקצועית", description: "אפשר לבחור שירות עיצוב אחד בלבד." },
    { id: "template-adjust", label: "התאמת עיצוב מוכן", description: "בחירה זו מחליפה שירות עיצוב אחר שנבחר." },
    { id: "full-design", label: "עיצוב מלא", description: "בחירה זו מחליפה שירות עיצוב אחר שנבחר." },
    { id: "rush", label: "ייצור דחוף", description: "נבחר אוטומטית לתאריך מוקדם מחמישה ימי ייצור, בתוספת 50 ₪." },
  ],
  artworkOptions: [
    { mode: "upload-now", label: "העלאת קובץ עכשיו", description: "אפשר להעלות קובץ מוכן או ליצור קובץ בעורך האונליין.", available: true },
    { mode: "send-later", label: "אשלח את הקובץ אחר כך", description: "אפשר להשלים את ההזמנה ולשלוח את הקובץ לאחר מכן.", available: true },
    { mode: "request-upload-link", label: "קישור להעלאה ממחשב אחר", description: "חיבור קישור ההעלאה יופעל בשלב הבא.", available: false },
    { mode: "need-design", label: "אני צריך עיצוב", description: "בחרו גם את שירות העיצוב המתאים בתוספות.", available: true },
  ],
};

const PVC_EXCLUSIVE_ADDON_GROUPS: readonly (readonly string[])[] = [
  ["holes", "eyelets", "rounded-corners"],
  ["file-check", "template-adjust", "full-design"],
];

function exclusiveGroupFor(addonId: string): readonly string[] | null {
  return PVC_EXCLUSIVE_ADDON_GROUPS.find(group => group.some(id => id === addonId)) ?? null;
}

async function removeConflictingAddonSelections(
  page: ProductPageController,
  configurator: ConfiguratorController,
  addonId: string,
): Promise<string[]> {
  const group = exclusiveGroupFor(addonId);
  if (!group) return [];
  const removed: string[] = [];
  for (const selectedId of configurator.getState().addonIds) {
    if (selectedId !== addonId && group.some(id => id === selectedId)) {
      await page.onAddonChange(selectedId, false);
      removed.push(selectedId);
    }
  }
  return removed;
}

async function normalizeLegacyExclusiveSelections(
  page: ProductPageController,
  configurator: ConfiguratorController,
): Promise<string[]> {
  const removed: string[] = [];
  for (const group of PVC_EXCLUSIVE_ADDON_GROUPS) {
    const selectedInGroup = configurator.getState().addonIds.filter(selectedId => group.some(id => id === selectedId));
    for (const selectedId of selectedInGroup.slice(1)) {
      await page.onAddonChange(selectedId, false);
      removed.push(selectedId);
    }
  }
  return removed;
}

function withStorefrontAddonRules(
  core: ProductPageController,
  configurator: ConfiguratorController,
): ProductPageController {
  return new Proxy(core, {
    get(target, property, receiver) {
      if (property === "onAddonChange") {
        return async (addonId: string, selected: boolean) => {
          if (selected) await removeConflictingAddonSelections(target, configurator, addonId);
          await target.onAddonChange(addonId, selected);
        };
      }
      if (property === "init") {
        return async () => {
          const outcome = await target.init();
          const removed = await normalizeLegacyExclusiveSelections(target, configurator);
          return {
            ...outcome,
            removedAddonIds: [...new Set([...outcome.removedAddonIds, ...removed])].sort(),
          };
        };
      }
      return Reflect.get(target, property, receiver);
    },
  });
}

const browserArtworkEnvironment: ArtworkEnvironment = {
  isVisible: () => document.visibilityState !== "hidden",
  isOnline: () => navigator.onLine !== false,
  onVisibilityChange(listener) {
    document.addEventListener("visibilitychange", listener);
    return () => document.removeEventListener("visibilitychange", listener);
  },
};

const browserDraftStorage: DraftStorage = {
  getItem(key) { return globalThis.sessionStorage.getItem(key); },
  setItem(key, value) { globalThis.sessionStorage.setItem(key, value); },
  removeItem(key) { globalThis.sessionStorage.removeItem(key); },
};

function createMutationId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `pvc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export type PvcStorefrontRuntimeOptions = {
  editLine?: PvcStorefrontCartLine;
};

/**
 * A homepage quick-order widget may hand off width/height/quantity via the
 * querystring. Only fully valid values (within the authoritative schema
 * constraints) are honored — a partial or malformed query never produces a
 * partially-overridden configuration, and a direct visit to /pvc-sign with
 * no querystring is completely unaffected.
 */
export function parsePvcQuickOrderQuery(
  search: string,
  schema: ConfiguratorSchema,
): { widthCm: number; heightCm: number; quantity: number } | null {
  const params = new URLSearchParams(search);
  const widthRaw = params.get("width");
  const heightRaw = params.get("height");
  const quantityRaw = params.get("quantity");
  if (widthRaw === null || heightRaw === null || quantityRaw === null) return null;

  const width = Number(widthRaw);
  const height = Number(heightRaw);
  const quantity = Number(quantityRaw);
  const { constraints } = schema;

  const widthError = validateDimension(Number.isFinite(width) ? width : null, {
    min: constraints.minWidthCm,
    max: constraints.maxWidthCm,
    step: constraints.stepCm,
  });
  const heightError = validateDimension(Number.isFinite(height) ? height : null, {
    min: constraints.minHeightCm,
    max: constraints.maxHeightCm,
    step: constraints.stepCm,
  });
  const quantityError = validateQuantity(quantity, constraints);
  if (widthError || heightError || quantityError) return null;

  return { widthCm: width, heightCm: height, quantity };
}

export function createPvcStorefrontRuntime(options: PvcStorefrontRuntimeOptions = {}): PvcSignRuntime {
  const endpoint = import.meta.env.VITE_SHOP_API_URL || "/shop-api";
  const client = createShopApiGraphqlClient({ endpoint });
  const editLine = options.editLine;
  const queryOverrides = editLine
    ? null
    : parsePvcQuickOrderQuery(window.location.search, PVC_STOREFRONT_SCHEMA);
  const initial = editLine?.configuration ?? {
    widthCm: queryOverrides?.widthCm ?? 70,
    heightCm: queryOverrides?.heightCm ?? 50,
    quantity: queryOverrides?.quantity ?? 1,
    materialId: "pvc-3mm",
    addonIds: [],
    requestedDate: null,
  };

  const configurator = createConfiguratorController({ schema: PVC_STOREFRONT_SCHEMA, initial });
  const pricing = createPricingController({ gateway: createPvcQuoteGateway({ client, binding: pvcQuoteBinding }) });
  const artwork = createArtworkController({
    gateway: createPreorderArtworkGateway(client, { endpoint }),
    environment: browserArtworkEnvironment,
    availableModes: ["upload-now", "send-later", "need-design"],
  });

  if (editLine) {
    const needsDesign = editLine.configuration.addonIds.some(id =>
      id === "file-check" || id === "template-adjust" || id === "full-design",
    );
    void artwork.setMode(needsDesign ? "need-design" : "send-later");
  }

  const orderLine = createOrderLineController({
    gateway: createPvcOrderLineGateway({ client, bindings: pvcOrderBindings }),
    mutationIds: { create: createMutationId },
  });

  const orderContext = editLine
    ? { type: "edit" as const, orderLineId: editLine.id, orderLineVersion: editLine.orderLineVersion }
    : { type: "create" as const };

  const snapshot = (): PvcDraftSourceSnapshot => ({
    productSchemaVersion: PVC_STOREFRONT_SCHEMA.schemaVersion,
    configurator: configurator.getDraftSnapshot(),
    artwork: artwork.getDraftSnapshot(),
    orderContext,
  });

  const draft = createDraftController({
    storage: browserDraftStorage,
    getSnapshot: snapshot,
    storageKey: editLine
      ? `happyprint:pvc-sign:edit:${editLine.id}:draft:v1`
      : "happyprint:pvc-sign:draft:v1",
  });

  const corePage = createProductPageController({
    configurator,
    pricing,
    artwork,
    orderLine,
    draft,
    schemaVersion: PVC_STOREFRONT_SCHEMA.schemaVersion,
    orderContext,
  });
  const page = withStorefrontAddonRules(corePage, configurator);
  return { page, configurator, pricing, artwork, orderLine, draft };
}
